/* import __COLOCATED_TEMPLATE__ from './phone-details-call-transcription.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import Component from '@glimmer/component';
import type { CallRecording } from 'embercom/objects/inbox/call-recording';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

interface PhoneDetailsCallTranscriptionSignature {
  Args: {
    recording: CallRecording;
  };
  Element: HTMLDivElement;
}

type TranscriptMessage = {
  speaker?: string;
  speaker_label?: string;
  start_time: string;
  content: string;
};

type Option = {
  text: string;
  icon: string;
  onSelectItem: () => void;
  isDestructive: boolean;
  isDisabled: boolean;
};

export default class PhoneDetailsCallTranscription extends Component<PhoneDetailsCallTranscriptionSignature> {
  @service declare intl: IntlService;

  @tracked showConfirmationModal = false;

  private readonly VOICEMAIL_PREFIX = 'voic';
  private readonly SPEAKER_PREFIX = 'spk_';
  private readonly PREFIX_LENGTH = 4;

  get recordingUrl(): string {
    return this.args.recording.recordingUrl;
  }

  get transcriptUrl(): string {
    return this.args.recording.transcriptUrl ?? '';
  }

  get recordingDeleted(): boolean {
    return this.args.recording.isDeleted;
  }

  get transcript(): TranscriptMessage[] {
    return this.args.recording.jsonTranscript ?? [];
  }

  get hasTranscript(): boolean {
    return !!this.args.recording.jsonTranscript?.length;
  }

  get transcriptStatusText() {
    if (
      this.args.recording.transcriptStatus === 'not_submitted' ||
      this.args.recording.transcriptStatus === 'submitted'
    ) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.processing');
    }

    if (this.recordingDeleted) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.deleted');
    }

    return this.intl.t('inbox.phone-details-sidebar.transcript.none');
  }

  get dropdownOptions(): Array<{ items: Option[] }> {
    let optionList: Option[];

    let downloadRecording: Option = {
      text: this.intl.t('inbox.phone-details-sidebar.download.recording'),
      icon: 'download',
      onSelectItem: () => (this.recordingUrl ? this.downloadFile(this.recordingUrl) : undefined),
      isDestructive: false,
      isDisabled: !this.recordingUrl || this.recordingDeleted,
    };

    let downloadTranscript: Option = {
      text: this.intl.t('inbox.phone-details-sidebar.download.transcript'),
      icon: 'download',
      onSelectItem: () => (this.transcriptUrl ? this.downloadFile(this.transcriptUrl) : undefined),
      isDestructive: false,
      isDisabled: !this.transcriptUrl || this.recordingDeleted,
    };

    let deleteAll: Option = {
      text: this.intl.t('inbox.phone-details-sidebar.delete-all'),
      icon: 'trash',
      onSelectItem: () => {
        this.enableConfirmationModal();
      },
      isDestructive: true,
      isDisabled: !this.recordingUrl || this.recordingDeleted,
    };

    optionList = [downloadRecording, downloadTranscript, deleteAll];

    return [{ items: optionList }];
  }

  private downloadFile(url: string): void {
    safeWindowOpen(url, '_blank');
  }

  @action
  speakerLabel(message: any) {
    let speaker = message.speaker || message.speaker_label;

    switch (speaker.substring(0, this.PREFIX_LENGTH)) {
      case this.VOICEMAIL_PREFIX:
        return this.intl.t('inbox.phone-details-sidebar.speakers.voicemail');
      case this.SPEAKER_PREFIX: {
        let speaker_number =
          (parseInt(speaker.substring(this.PREFIX_LENGTH, speaker.length), 10) || 0) + 1;
        return `${this.intl.t('inbox.phone-details-sidebar.speakers.speaker', {
          speakerIdentifier: speaker_number,
        })}`;
      }
      default:
        return speaker;
    }
  }

  @action
  enableConfirmationModal() {
    this.showConfirmationModal = true;
  }

  @action
  performDelete() {
    this.showConfirmationModal = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::PhoneDetailsCallTranscription': typeof PhoneDetailsCallTranscription;
    'inbox2/phone-details-call-transcription': typeof PhoneDetailsCallTranscription;
  }
}
